import Typography from "@mui/material/Typography";
import { hasMenu } from "auth/access";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "modules/auth/auth.context";
import {
  ApproveRejectActions,
  ExpandableTable,
  InlineRejectionForm,
  SectionTitle,
  SegmentedControl,
} from "modules/shared/components";
import { capitalizeString } from "modules/shared/utils";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { logError } from "utils/logger";
import { AdminLeaveApplicationModal } from "../components";
import {
  CLOSED_STATUS_OPTIONS,
  LEAVE_ACTION_MODES,
  LEAVE_CLOSED_STATUSES,
  LEAVE_PRIMARY_VIEWS,
  LEAVE_STATUS,
  LEAVE_STATUS_KEYS,
  PRIMARY_VIEW_OPTIONS,
} from "../constants";
import { useAdminLeaves } from "../hooks/useAdminLeaves";
import { approveLeave, rejectLeave } from "../service";
import { getEmptyMessage, getVisibleLeaves } from "../utils";

/**
 * @param {Object} props
 * @param {'active' | 'closed'} props.view
 * @returns {Array<{id: string, label: string, dataKey: string, renderCell: (value: any) => JSX.Element}>}
 */
function getAdminLeavesColumns({ view }) {
  const columns = [
    {
      id: "employeeName",
      label: "Name",
      dataKey: "employeeName",
      sortable: true,
    },
    {
      id: "department",
      label: "Department",
      dataKey: "department",
      sortable: true,
      renderCell: capitalizeString,
    },
    {
      id: "leaveType",
      label: "Leave type",
      dataKey: "leaveType",
      sortable: true,
    },
    {
      id: "startDate",
      label: "Start date",
      dataKey: "startDate",
      renderCell: (_, row) => (
        <LeaveSession date={row.startDate} session={row.startSession} />
      ),
      sortable: true,
    },
    {
      id: "endDate",
      label: "End date",
      dataKey: "endDate",
      renderCell: (_, row) => (
        <LeaveSession date={row.endDate} session={row.endSession} />
      ),
      sortable: true,
    },
  ];

  if (view === LEAVE_PRIMARY_VIEWS.CLOSED) {
    (columns as any[]).push({
      id: "status",
      label: "Status",
      dataKey: "status",
      sortable: false,
      renderCell: (val: any) => {
        const config = LEAVE_STATUS[val];

        if (!config) {
          return <span>-</span>;
        }
        return (
          <Typography sx={{ color: `${config.color}.main`, fontWeight: 500 }}>
            {config.label ?? "-"}
          </Typography>
        );
      },
    });
  }

  return columns;
}

export default function AdminLeaves() {
  const { user } = useAuth();
  const actorEmployeeId = user?.employee_id;

  const { leavesByStatus, loading, refresh } = useAdminLeaves();

  const [view, setView] = useState(LEAVE_PRIMARY_VIEWS.ACTIVE);
  const [closedStatus, setClosedStatus] = useState(
    LEAVE_CLOSED_STATUSES.APPROVED
  );
  const [expandedRowId, setExpandedRowId] = useState(null);

  const [actionMode, setActionMode] = useState({
    type: LEAVE_ACTION_MODES.IDLE,
    leaveId: null,
  });
  const [updating, setUpdating] = useState(false);
  const visibleLeaves = useMemo(() => {
    return getVisibleLeaves(leavesByStatus, view, closedStatus);
  }, [view, closedStatus, leavesByStatus]);

  const rejectionForm = useForm({ defaultValues: { rejectionReason: "" } });
  const { resetField, trigger, watch } = rejectionForm;
  const rejectionReason = watch("rejectionReason");

  const emptyMessage = getEmptyMessage(view, closedStatus);

  const columns = useMemo(() => getAdminLeavesColumns({ view }), [view]);

  const handleApprove = async (leaveId) => {
    if (!leaveId || !actorEmployeeId || updating) {
      return;
    }

    setUpdating(true);
    try {
      await approveLeave(leaveId, actorEmployeeId);
      toast.success("Leave application approved.");
      setActionMode({ type: LEAVE_ACTION_MODES.IDLE, leaveId: null });
      await refresh();
    } catch (err) {
      toast.error(
        logError({
          message: "Failed to approve leave application",
          error: err,
          code: "LEAVE_APPROVE_FAILED",
        })
      );
    } finally {
      setUpdating(false);
    }
  };

  const startReject = (leaveId) => {
    if (updating) return;
    setActionMode({ type: LEAVE_ACTION_MODES.REJECT, leaveId });
  };

  const resetReject = useCallback(() => {
    setActionMode({ type: LEAVE_ACTION_MODES.IDLE, leaveId: null });
    resetField("rejectionReason");
  }, [resetField]);

  const handleReject = async (leaveId) => {
    const isValid = await trigger("rejectionReason");
    if (!isValid || !actorEmployeeId || updating) return;

    const reason = rejectionReason?.trim();
    if (!reason) return;

    setUpdating(true);
    try {
      await rejectLeave(leaveId, reason, actorEmployeeId);
      toast.success("Leave application rejected.");
      resetReject();
      await refresh();
    } catch (err) {
      toast.error(
        logError({
          message: "Failed to reject leave application",
          error: err,
          code: "LEAVE_REJECT_FAILED",
        })
      );
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    resetReject();
  }, [expandedRowId, view, closedStatus, resetReject]);

  return (
    <div className="column gap-4">
      <SectionTitle
        title="LEAVES"
        action={<AdminLeaveApplicationModal onRefresh={refresh} />}
      />

      <div className="column gap-3">
        <SegmentedControl
          options={PRIMARY_VIEW_OPTIONS}
          value={view}
          onChange={(v: string) => setView(v)}
          ariaLabel="Leave view"
        />

        {view === LEAVE_PRIMARY_VIEWS.CLOSED && (
          <SegmentedControl
            options={CLOSED_STATUS_OPTIONS}
            value={closedStatus}
            onChange={(v: string) => setClosedStatus(v)}
            ariaLabel="Closed leave status"
          />
        )}
      </div>

      <ExpandableTable
        columns={columns}
        data={visibleLeaves}
        loading={loading}
        emptyMessage={emptyMessage}
        onExpandedRowChange={setExpandedRowId}
        renderExpandedRow={(leave) => (
          <AdminLeavesExpandedRow
            leaveData={leave}
            updating={updating}
            actionMode={actionMode}
            onApprove={handleApprove}
            onSubmitReject={handleReject}
            onStartReject={startReject}
            onCancelReject={resetReject}
            rejectionForm={rejectionForm}
          />
        )}
      />
    </div>
  );
}

/**
 * @param {Object} props
 * @param {string} props.date
 * @param {string} props.session
 * @returns {JSX.Element}
 */
function LeaveSession({ date, session }) {
  if (!date) {
    return <span className="unavailable-message">—</span>;
  }

  return (
    <div className="column gap-1">
      <span>{date}</span>
      {session && <span>({session})</span>}
    </div>
  );
}

/**
 * @param {Object} props
 * @param {Object} props.leaveData
 * @param {boolean} props.updating
 * @param {Object} props.actionMode
 * @param {Function} props.onApprove
 * @param {Function} props.onSubmitReject
 * @param {Function} props.onStartReject
 * @param {Function} props.onCancelReject
 * @param {Object} props.rejectionForm
 * @returns {JSX.Element}
 */
function AdminLeavesExpandedRow({
  leaveData,
  updating,
  actionMode,
  rejectionForm,
  onApprove,
  onSubmitReject,
  onStartReject,
  onCancelReject,
}) {
  return (
    <div className="column">
      <div className="row row--between gap-5">
        <div>
          <span>Number of days: </span>
          <span className="font-medium">{leaveData.numberOfDays}</span>
        </div>
        <div>
          <span>Applied on: </span>
          <span className="font-medium">{leaveData.appliedOn}</span>
        </div>
        <div className="row gap-3">
          <span className="font-medium">Leave reason</span>
          <p>{leaveData.reason || "—"}</p>
        </div>
      </div>
      {leaveData.status === LEAVE_STATUS_KEYS.REJECTED && (
        <div className="row gap-3">
          <span className="font-medium">Reason for Rejection</span>
          <p>{leaveData.rejectionReason || "-"}</p>
        </div>
      )}
      {/* Approving is the APPROVE action of Leave Approvals; without it the queue is read-only. */}
      {leaveData.status === LEAVE_STATUS_KEYS.PENDING && hasMenu('hr.leave-approvals', 'APPROVE') && (
        <>
          {actionMode.type === LEAVE_ACTION_MODES.IDLE && (
            <ApproveRejectActions
              loading={updating}
              onApprove={() => onApprove(leaveData.id)}
              onReject={() => onStartReject(leaveData.id)}
            />
          )}

          {actionMode.type === LEAVE_ACTION_MODES.REJECT &&
            actionMode.leaveId === leaveData.id && (
              <InlineRejectionForm
                methods={rejectionForm}
                disabled={updating}
                onCancel={onCancelReject}
                onSubmit={() => onSubmitReject(leaveData.id)}
              />
            )}
        </>
      )}
    </div>
  );
}
